// src/components/ui/ListeDeroulante.jsx
// ============================================================================
// La liste déroulante de KLASYS : celle de la Vie scolaire
// ============================================================================
//
// Décision de Daniel du 04/11/2026 : toute liste déroulante prend la forme de
// celle du module Vie scolaire (`AppSelect`, VieScolairePage.jsx) - un champ
// blanc de 36 px à chevron, une liste claire où le choix courant est en bleu
// sur fond teinté, sans coche, et une recherche en tête quand la liste est
// longue. DESIGN-SYSTEM.md, section « Listes déroulantes ».
//
// Ce qui change par rapport au modèle : la liste s'ouvre dans un Popover Radix
// rendu hors de la page, en mode modal. Posée en absolu comme dans la Vie
// scolaire, elle était coupée par le corps d'une fenêtre (`overflow-y-auto`) ;
// hors de la page mais non modale, la molette ne la faisait plus défiler dans
// une fenêtre (le verrou de défilement du Dialog l'en empêchait). En modal, le
// dernier verrou ouvert, le sien, est le seul actif.
//
// La liste passe au-dessus de toute fenêtre, même de celles des calques hauts
// (modalOverlay.js : le dossier de l'élève, z-[10000] ; une fenêtre
// imbriquée, z-[10020]) : ouverte depuis l'une d'elles, elle tombait dessous.
//
// `options` : `{ valeur, libelle, desactive? }`. Les valeurs sont comparées en
// chaînes ; la valeur vide ('') est permise, pour « Tous », « Aucun »…
// `groupes` : `{ libelle, options }`, une liste à en-têtes ; l'emporte sur
// `options`. `recherche` : la case « Rechercher… », d'office au-delà de dix
// choix.
// ============================================================================

import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { ChevronDown, Search } from 'lucide-react';

const SEUIL_RECHERCHE = 10;

// « Elève » trouve « Élève » : la recherche ignore accents et casse.
const plier = (texte) => String(texte ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const versChaine = (v) => (v === undefined || v === null ? '' : String(v));

const ListeDeroulante = ({
  id,
  valeur,
  onChange,
  options = [],
  groupes = null,
  placeholder = 'Sélectionner…',
  recherche,
  desactive = false,
  className = '',
  'aria-label': ariaLabel
}) => {
  const [ouvert, setOuvert] = useState(false);
  const [terme, setTerme] = useState('');
  const [actif, setActif] = useState(-1);
  const champRecherche = useRef(null);
  const liste = useRef(null);
  const courant = versChaine(valeur);

  // Une seule suite de lignes : les en-têtes de groupe et les choix.
  const lignes = useMemo(() => {
    const normaliser = (o) => ({ valeur: versChaine(o.valeur), libelle: String(o.libelle ?? ''), desactive: Boolean(o.desactive) });
    if (Array.isArray(groupes) && groupes.length > 0) {
      return groupes.flatMap((g) => [{ entete: g.libelle }, ...(g.options || []).map(normaliser)]);
    }
    return options.map(normaliser);
  }, [options, groupes]);

  const choix = lignes.filter((l) => !l.entete);
  const choisi = choix.find((o) => o.valeur === courant);
  const avecRecherche = recherche ?? choix.length > SEUIL_RECHERCHE;

  const visibles = useMemo(() => {
    if (!terme.trim()) return lignes;
    const t = plier(terme.trim());
    // Un en-tête ne reste que s'il garde au moins un choix sous lui.
    const gardes = [];
    let entete = null;
    for (const l of lignes) {
      if (l.entete) { entete = l; continue; }
      if (!plier(l.libelle).includes(t)) continue;
      if (entete) { gardes.push(entete); entete = null; }
      gardes.push(l);
    }
    return gardes;
  }, [lignes, terme]);

  const selectionnables = visibles.filter((l) => !l.entete && !l.desactive);

  // À l'ouverture, le choix courant est mis en avant ; à chaque frappe, le premier trouvé.
  useEffect(() => {
    if (!ouvert) return;
    const i = selectionnables.findIndex((o) => o.valeur === courant);
    setActif(i >= 0 && !terme ? i : (selectionnables.length ? 0 : -1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvert, terme]);

  useEffect(() => {
    if (!ouvert || actif < 0) return;
    const element = liste.current?.querySelector(`[data-rang="${actif}"]`);
    element?.scrollIntoView({ block: 'nearest' });
  }, [ouvert, actif]);

  const basculer = (suivant) => {
    setOuvert(suivant);
    if (!suivant) setTerme('');
  };

  const prendre = (option) => {
    if (!option || option.desactive) return;
    basculer(false);
    if (option.valeur !== courant) onChange?.(option.valeur);
  };

  const clavier = (e) => {
    const n = selectionnables.length;
    if (e.key === 'ArrowDown') { e.preventDefault(); if (n) setActif((a) => (a + 1) % n); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); if (n) setActif((a) => (a <= 0 ? n - 1 : a - 1)); }
    else if (e.key === 'Home') { if (!avecRecherche) { e.preventDefault(); if (n) setActif(0); } }
    else if (e.key === 'End') { if (!avecRecherche) { e.preventDefault(); if (n) setActif(n - 1); } }
    else if (e.key === 'Enter') { e.preventDefault(); prendre(selectionnables[actif]); }
    else if (e.key === 'Tab') { basculer(false); }
  };

  let rang = -1;

  return (
    <PopoverPrimitive.Root open={ouvert} onOpenChange={basculer} modal>
      <PopoverPrimitive.Trigger asChild disabled={desactive}>
        <button
          id={id}
          type="button"
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          onKeyDown={(e) => {
            if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !ouvert) { e.preventDefault(); basculer(true); }
          }}
          className={`flex h-9 w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 text-left text-sm outline-none transition hover:border-gray-300 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15 data-[state=open]:border-[#246990] data-[state=open]:ring-2 data-[state=open]:ring-[#246990]/15 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:hover:border-gray-200 ${className}`}
        >
          <span className={`truncate ${choisi ? (desactive ? 'text-gray-500' : 'text-gray-800') : 'text-gray-400'}`}>
            {choisi?.libelle || placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            (avecRecherche ? champRecherche.current : liste.current)?.focus();
          }}
          onKeyDown={clavier}
          className="z-[10100] w-[var(--radix-popover-trigger-width)] min-w-[12rem] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md outline-none"
        >
          {avecRecherche && (
            <div className="border-b border-gray-100 p-2">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" aria-hidden="true" />
                <input
                  ref={champRecherche}
                  type="text"
                  value={terme}
                  onChange={(e) => setTerme(e.target.value)}
                  className="h-9 w-full rounded-lg border border-gray-200 pl-9 pr-3 text-sm outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                  placeholder="Rechercher…"
                  aria-label="Rechercher dans la liste"
                  autoComplete="off"
                />
              </div>
            </div>
          )}

          <div
            ref={liste}
            role="listbox"
            tabIndex={-1}
            aria-label={ariaLabel}
            aria-activedescendant={actif >= 0 && id ? `${id}-choix-${actif}` : undefined}
            className="max-h-64 overflow-y-auto outline-none"
          >
            {selectionnables.length === 0 && !visibles.some((l) => !l.entete) ? (
              <div className="px-4 py-3 text-center text-sm text-gray-500">{terme ? 'Aucun résultat' : 'Aucun choix'}</div>
            ) : (
              visibles.map((l, i) => {
                if (l.entete) {
                  return (
                    <div key={`entete-${l.entete}-${i}`} className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      {l.entete}
                    </div>
                  );
                }
                const r = l.desactive ? null : (rang += 1);
                const estChoisi = l.valeur === courant;
                const enAvant = r !== null && r === actif;
                return (
                  <button
                    key={l.valeur || 'vide'}
                    id={id && r !== null ? `${id}-choix-${r}` : undefined}
                    type="button"
                    role="option"
                    tabIndex={-1}
                    aria-selected={estChoisi}
                    aria-disabled={l.desactive || undefined}
                    data-rang={r ?? undefined}
                    disabled={l.desactive}
                    onMouseEnter={() => { if (r !== null) setActif(r); }}
                    onClick={() => prendre(l)}
                    className={`block w-full px-3 py-2 text-left text-sm transition-colors ${l.desactive
                      ? 'cursor-not-allowed text-gray-300'
                      : `${estChoisi ? 'font-medium text-[#246990]' : 'text-gray-800'} ${estChoisi || enAvant ? 'bg-[#eef7fb]' : ''}`}`}
                  >
                    {l.libelle}
                  </button>
                );
              })
            )}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
};

export default ListeDeroulante;
